import { useEffect, useState } from 'react';
import {
  ACCOUNT_TYPE_LABEL,
  ACCOUNT_TYPE_ORDER,
  chartOfAccountsApi,
  type Account,
  type AccountBalanceSide,
  type AccountType,
  type CreateAccountInput,
} from '../lib/api/chart-of-accounts';

export type AccountFormMode = 'create' | 'edit';

interface Props {
  mode: AccountFormMode;
  editing?: Account;
  parent?: Account;
  onClose: () => void;
  onSaved: () => void;
}

export function AccountFormModal({ mode, editing, parent, onClose, onSaved }: Props) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [parentCode, setParentCode] = useState<string>('');
  const [type, setType] = useState<AccountType>('asset');
  const [defaultBalance, setDefaultBalance] = useState<AccountBalanceSide>('debit');
  const [isLeaf, setIsLeaf] = useState(true);
  const [notes, setNotes] = useState('');
  const [active, setActive] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setErr(null);
    setSaving(false);
    if (mode === 'edit' && editing) {
      setCode(editing.code);
      setName(editing.name);
      setParentCode(editing.parentCode ?? '');
      setType(editing.type);
      setDefaultBalance(editing.defaultBalance);
      setIsLeaf(editing.isLeaf);
      setActive(editing.active);
      setNotes(editing.notes ?? '');
    } else if (mode === 'create') {
      setCode('');
      setName('');
      setParentCode(parent?.code ?? '');
      setType(parent?.type ?? 'asset');
      setDefaultBalance(parent?.defaultBalance ?? 'debit');
      setIsLeaf(true);
      setActive(true);
      setNotes('');
    }
  }, [mode, editing, parent]);

  const title = mode === 'create' ? (parent ? 'Thêm TK con mới' : 'Thêm tài khoản mới') : `Sửa tài khoản ${editing?.code}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);

    if (mode === 'create') {
      if (!/^\d{2,20}$/.test(code.trim())) return setErr('Mã TK phải là số, từ 2-20 ký tự.');
      if (name.trim().length < 2) return setErr('Tên TK tối thiểu 2 ký tự.');
    } else {
      if (name.trim().length < 2) return setErr('Tên TK tối thiểu 2 ký tự.');
    }

    setSaving(true);
    try {
      if (mode === 'create') {
        const payload: CreateAccountInput = {
          code: code.trim(),
          name: name.trim(),
          parentCode: parentCode.trim() || undefined,
          type,
          defaultBalance,
          isLeaf,
          active,
          notes: notes.trim() || undefined,
        };
        await chartOfAccountsApi.create(payload);
      } else if (editing) {
        await chartOfAccountsApi.update(editing.code, {
          name: name.trim(),
          active,
          defaultBalance,
          notes: notes.trim() || undefined,
        });
      }
      onSaved();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : String(e2));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editing || !window.confirm(`Xoá TK ${editing.code} - ${editing.name}?`)) return;
    setSaving(true);
    try {
      await chartOfAccountsApi.remove(editing.code);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cvm-backdrop" onClick={onClose}>
      <div className="cvm-modal afm-modal" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="cvm-head afm-proto-head">
            <div className="afm-proto-heading">
              <div className="afm-proto-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" /></svg></div>
              <div><h2>{title}</h2><p>Cấu hình TK sẽ dùng trong toàn công ty</p></div>
            </div>
            <button type="button" className="cvm-close afm-proto-close" onClick={onClose} title="Đóng">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </button>
          </div>

          <div className="cvm-body afm-proto-body">
            <section className="afm-proto-card">
              <div className="afm-grid-2 afm-proto-grid-main">
              <div className="pfm-field">
                <label>
                  Mã TK <span className="req">*</span>
                </label>
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="VD 11121"
                  disabled={mode === 'edit'}
                  required
                  inputMode="numeric"
                />
              </div>
              <div className="pfm-field">
                <label>
                  Tên tài khoản <span className="req">*</span>
                </label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="VD Tiền mặt VND"
                  required
                />
              </div>
              </div>
              <div className="afm-grid-2">
              <div className="pfm-field">
                <label>{parent ? 'TK cha' : 'Loại TK'}</label>
                {parent ? <div className="afm-parent-readonly">{parent.code} · {parent.name}</div> : <select value={type} onChange={(e) => setType(e.target.value as AccountType)} disabled={mode === 'edit'}>{ACCOUNT_TYPE_ORDER.map((t) => <option key={t} value={t}>{ACCOUNT_TYPE_LABEL[t]}</option>)}</select>}
              </div>
              <div className="pfm-field">
                <label>Số dư mặc định</label>
                <select
                  value={defaultBalance}
                  onChange={(e) => setDefaultBalance(e.target.value as AccountBalanceSide)}
                >
                  <option value="debit">Dư Nợ</option>
                  <option value="credit">Dư Có</option>
                </select>
              </div>
              </div>

            {mode === 'create' && !parent && (
              <div className="pfm-field">
                <label>TK cha (nếu là TK cấp con)</label>
                <input
                  value={parentCode}
                  onChange={(e) => setParentCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="VD 111 (bỏ trống nếu là TK cấp 1)"
                  inputMode="numeric"
                />
              </div>
            )}
            </section>

            <label className="afm-status-card">
              <span><b>Trạng thái sử dụng</b><small>TK đang {active ? 'bật' : 'tắt'} · {active ? 'Xuất hiện trong dropdown chọn TK và có thể hạch toán.' : 'Không xuất hiện khi lập chứng từ mới.'}</small></span>
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              <span className={`pfm-switch ${active ? 'on' : ''}`}><span className="pfm-switch-dot" /></span>
            </label>

            <section className="afm-proto-card afm-notes-card"><div className="pfm-field">
              <label>Ghi chú</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="VD: STK chi lương chính, mở tại VietinBank chi nhánh Sài Gòn…"
                rows={3}
              />
            </div></section>

            {err && <div className="cvm-error">{err}</div>}
          </div>

          <div className="cvm-foot afm-proto-foot">
            {mode === 'edit' ? <button type="button" className="afm-delete-btn" onClick={handleDelete} disabled={saving}>Xoá TK</button> : <span />}
            <div className="afm-proto-actions">
            <button type="button" className="pay-btn-outline" onClick={onClose} disabled={saving}>
              Huỷ
            </button>
            <button type="submit" className="pay-btn-dark" disabled={saving}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 4 4L19 6" /></svg>{saving ? 'Đang lưu…' : 'Lưu'}
            </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
